import { WorkflowsSidebarSection } from '@/composition/hosts/agents/routines/WorkflowsSidebarSection';
import { useIsWorkflowsEnabled } from '@/features/flags';
import { useIsFhirAuth } from '@/hooks/integrations/useIsFhirAuth';
import {
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
} from '@heidi/design-system/page-shell';
import { EvidenceHomeNavItem } from '../sidebar/nav-items/EvidenceHomeNavItem';
import { NewChatNavItem } from '../sidebar/nav-items/NewChatNavItem';
import { RoutinesNavItem } from '../sidebar/nav-items/RoutinesNavItem';
import { AgentHomeNavProvider } from './agent-home-nav-context';
import { PinnedChatsSection } from './pinned-chats/pinned-chats-section';
import { RecentsSection } from './recents/recents-section';
import { SavedChatsProvider } from './saved-chats-context';

/**
 * The nav's top-level `SidebarGroup`s (New chat/Routines/Evidence, Scheduled,
 * Pinned, Recents) are direct children of `SidebarContent`'s inner
 * `[data-slot=sidebar-content]` div. Scoping the group-spacing rules below
 * through it — rather than a bare `[data-sidebar=group]` descendant selector
 * — is what keeps them from also reaching Recents' nested per-date
 * `SidebarGroup`s ("Today", "Yesterday", …), which sit deeper and would
 * otherwise pick up the same `py-0`/`pt-2` (PR #23761 review). Exported so
 * the selector-proof test in `nav-v3-routines-recents-alignment.test.tsx`
 * derives its `:scope` selectors from this one source instead of a hand-typed
 * copy that could silently drift from what is actually rendered.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const NAV_V3_TOP_LEVEL_GROUP_SELECTOR =
  '[data-slot=sidebar-content]>[data-sidebar=group]';

// Tailwind's scanner only matches complete literal class strings in source
// text. A template literal that interpolates `NAV_V3_TOP_LEVEL_GROUP_SELECTOR`
// in here never appears as one contiguous token, so the scanner emits no CSS
// for it — the class still lands in the DOM, but with no matching rule. Each
// class below must stay a single, whole string literal with no `${}` inside.
const SIDEBAR_CONTENT_CLASSNAME =
  'scrollbar-none [&_[data-slot=sidebar-content]>[data-sidebar=group]:first-child]:pt-2 [&_[data-slot=sidebar-content]>[data-sidebar=group]]:py-0';

/** Home-only secondary destinations shown beside the primary sidebar. */
export function NavV3() {
  // We MUST hide creation entry points for SMART-on-FHIR launches for some
  // enterprises — same invariant as SideNav's create button.
  const canCreateNewChat = !useIsFhirAuth();
  const isWorkflowsEnabled = useIsWorkflowsEnabled();

  return (
    <AgentHomeNavProvider>
      <div
        className="group/sidebar-container relative flex size-full flex-col"
        data-testid="agent-home-nav-container"
      >
        <SidebarContent className={SIDEBAR_CONTENT_CLASSNAME}>
          <SidebarGroup>
            <SidebarMenu>
              {canCreateNewChat && <NewChatNavItem />}
              <RoutinesNavItem />
              <EvidenceHomeNavItem />
            </SidebarMenu>
          </SidebarGroup>

          {isWorkflowsEnabled && <WorkflowsSidebarSection isSidebarPanelEnabled />}
          <SavedChatsProvider>
            <PinnedChatsSection />
            <RecentsSection />
          </SavedChatsProvider>
        </SidebarContent>
      </div>
    </AgentHomeNavProvider>
  );
}
